import { createContext, useCallback, useContext } from 'react'
import { useScopedRead } from './useScopedRead'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { dedupe, getCached, invalidateCache, setCached } from '@/lib/query-cache'
import { resolveActiveOrganizationId } from '@/lib/organization-context'

export type OrgRole = 'owner' | 'admin' | 'member'

export interface Organization {
  id: string
  slug: string
  name: string
  email_domain: string | null
  domain_verified_at: string | null
  brand_settings_id: string | null
  billing_method: 'stripe' | 'invoice' | 'free'
  subscription_tier: 'starter' | 'pro' | 'enterprise'
  subscription_status: string
  join_policy: 'invite_only' | 'open_domain'
  /** Organisasjonens egen bryter: andres offentlige arrangementer i medlemmenes lister. */
  public_events_visible: boolean
  /** Organisasjonens egen bryter: Tilbud og Katalog. */
  offers_enabled: boolean
}

export interface OrganizationMembership {
  org: Organization
  role: OrgRole
  joined_at: string
  /** Set by EventRipple for one person in one organisation: shows «Tilbakemelding» (20261003132614). */
  feedback_enabled: boolean
}

interface UseMyOrganizationResult {
  /** All orgs the current user is a member of */
  memberships: OrganizationMembership[]
  /** The user's currently-active org (or null if personal) */
  active: OrganizationMembership | null
  /** Acknowledged active context for server-scoped cache keys. */
  activeOrgId: string | null
  loading: boolean
  error: boolean
  refetch: () => Promise<void>
  /** Switch active org (must be a member). Triggers refetch. */
  setActive: (orgId: string | null) => Promise<void>
}

/**
 * Returns the current user's organization context.
 *
 * Today's design (Clerk/Linear-style):
 * - A user can belong to N organizations
 * - `profiles.active_org_id` tracks which one is "current" for that user
 * - If not set, the oldest-joined org is the active default
 * - If the user belongs to no org, `active` is null (personal account)
 */
interface OrgSnapshot {
  memberships: OrganizationMembership[]
  activeOrgId: string | null
}

const MyOrganizationContext = createContext<UseMyOrganizationResult | null>(null)
export const MyOrganizationProvider = MyOrganizationContext.Provider

const EMPTY_MEMBERSHIPS: OrganizationMembership[] = []
const ORG_CACHE_KEY = 'my-organization:'
/**
 * Org membership changes when somebody is invited into an org or switches the
 * active one — not between two component mounts on the same screen. Both of
 * those paths bust the cache explicitly (`setActive` below, and the invite
 * flows through `invalidateMyOrganization`).
 */
const ORG_TTL_MS = 60_000
let contextVersion = 0

export function invalidateMyOrganization(): void {
  contextVersion++
  invalidateCache(ORG_CACHE_KEY)
}

export function useMyOrganization(): UseMyOrganizationResult {
  const shared = useContext(MyOrganizationContext)
  const user = useAuthStore((s) => s.user)
  const read = useCallback(async (): Promise<OrgSnapshot> => {
    if (!user) throw new Error('organization_account_required')
    const cacheKey = `${ORG_CACHE_KEY}${user.id}`
    const cached = getCached<OrgSnapshot>(cacheKey, ORG_TTL_MS)
    if (cached) return cached

    const requestedVersion = contextVersion
    const snapshot = await dedupe(cacheKey, async (): Promise<OrgSnapshot> => {
      // Memberships and the active-org pointer are independent lookups — the
      // profile row does not depend on which orgs came back. They used to run
      // one after the other, which cost a round-trip for nothing on every
      // screen that mounts `useSubscription` (sidebar, bottom nav, route
      // guards, every upgrade prompt).
      const [membershipResult, profileResult] = await Promise.all([
        supabase
          .from('organization_members')
          .select('org_id, role, joined_at, feedback_enabled')
          .eq('user_id', user.id)
          .order('joined_at', { ascending: true })
          .order('org_id', { ascending: true }),
        supabase.from('profiles').select('active_org_id').eq('id', user.id).maybeSingle(),
      ])

      if (membershipResult.error) throw membershipResult.error
      if (profileResult.error) throw profileResult.error
      if (!Array.isArray(membershipResult.data) || !profileResult.data) throw new Error('organization_context_unacknowledged')
      const memberRows = membershipResult.data
      const profileRow = profileResult.data
      const members = memberRows as Array<{
        org_id: string
        role: OrgRole
        joined_at: string
        feedback_enabled?: boolean | null
      }>

      // Separate queries rather than a nested embed: the new tables have no FK
      // metadata introspected for PostgREST yet.
      let list: OrganizationMembership[] = []
      if (members.length > 0) {
        const orgIds = members.map((m) => m.org_id)
        const { data: orgRows, error: orgError } = await supabase
          .from('organizations')
          .select('id, slug, name, email_domain, domain_verified_at, brand_settings_id, billing_method, subscription_tier, subscription_status, join_policy, public_events_visible, offers_enabled')
          .in('id', orgIds)

        if (orgError) throw orgError
        if (!Array.isArray(orgRows) || orgRows.length !== orgIds.length) throw new Error('organization_metadata_unacknowledged')
        const orgById = new Map(
          ((orgRows ?? []) as Organization[]).map((o) => [o.id, o])
        )
        list = members
          .map((m) => {
            const org = orgById.get(m.org_id)
            if (!org) return null
            return { org, role: m.role, joined_at: m.joined_at, feedback_enabled: m.feedback_enabled === true }
          })
          .filter((x): x is OrganizationMembership => x !== null)
      }

      // A saved pointer is only a preference. Membership is authority: accept
      // the pointer while it still names a current membership, otherwise use
      // the same deterministic fallback as the server contract.
      const explicitActive = profileRow?.active_org_id ?? null
      return {
        memberships: list,
        // Membership is authority; the saved profile pointer is a preference.
        activeOrgId: resolveActiveOrganizationId(explicitActive, members),
      }
    })

    if (requestedVersion !== contextVersion) throw new Error('organization_context_changed')
    if (useAuthStore.getState().user?.id === user.id) setCached(cacheKey, snapshot)
    return snapshot
  }, [user?.id])

  const state = useScopedRead(shared ? null : 'my-organization', read)
  const { mutate, reload } = state
  const memberships = state.data?.memberships ?? EMPTY_MEMBERSHIPS
  const activeOrgId = state.data?.activeOrgId ?? null
  const refetch = useCallback(async () => {
    invalidateMyOrganization()
    await reload()
  }, [user?.id, reload])
  const setActive = useCallback(async (orgId: string | null) => {
    if (state.error || state.loading || !state.data) throw new Error('organization_context_unacknowledged')
    if (orgId !== null && !memberships.some(member => member.org.id === orgId)) throw new Error('organization_membership_required')
    await mutate(async () => {
      // Postgres accepts null to clear the preference; generated RPC arguments omit nullability.
      const { error } = await (supabase.rpc as (name: 'set_active_org', args: { p_org_id: string | null }) => PromiseLike<{ error: unknown }>)('set_active_org', { p_org_id: orgId })
      if (error) throw error
    }, snapshot => ({ memberships: snapshot?.memberships ?? EMPTY_MEMBERSHIPS, activeOrgId: orgId }))
    invalidateMyOrganization()
    // The transition route reloads every tenant-scoped consumer after this acknowledgement.
  }, [memberships, state.error, state.loading, state.data, mutate])
  const active = memberships.find(member => member.org.id === activeOrgId) ?? null
  return shared ?? { memberships, active, activeOrgId, loading: state.loading, error: state.error, refetch, setActive }
}
